import React, { useState, useEffect } from 'react';
import { 
  Bookmark, Copy, CheckCircle, AlertTriangle, ArrowRight, RefreshCw, Book, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import getApiUrl from '../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const API_URL = getApiUrl();

// ═══════════════════════════════════════════════════════════════
// COMPOSANT: DIRECTIVES POUR L'AGENT (FORK)
// ═══════════════════════════════════════════════════════════════
const AgentDirectives = () => {
  const [copied, setCopied] = useState(false);
  const [copiedPhrase, setCopiedPhrase] = useState(false);
  const [loading, setLoading] = useState(true);
  const [directives, setDirectives] = useState(null);

  // Charger les directives depuis l'API
  useEffect(() => {
    const fetchDirectives = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/project-directives`);
        if (response.ok) {
          const data = await response.json();
          setDirectives(data);
        }
      } catch (error) {
        console.error('Erreur chargement directives:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDirectives();
  }, []);

  const forkPhrase = directives?.fork_phrase || "Avant de reprendre, n'oublie pas de consulter les directives";
  const phraseACopier = directives?.phrase_a_copier || "Consulte les directives agents (/api/project-directives) et exécute immédiatement l'étape 1";
  const steps = directives?.steps || [];
  const importantNotes = directives?.important_notes || [];

  const copyForkPhrase = () => {
    navigator.clipboard.writeText(forkPhrase);
    setCopied(true);
    toast.success('Phrase copiée !');
    setTimeout(() => setCopied(false), 2000);
  };

  const copyPhraseACopier = () => {
    navigator.clipboard.writeText(phraseACopier);
    setCopiedPhrase(true);
    toast.success('Phrase copiée !');
    setTimeout(() => setCopiedPhrase(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600/20 to-orange-600/20 border border-amber-500/30 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <Bookmark className="w-8 h-8 text-amber-400" />
          <h2 className="text-2xl font-bold text-white">Directives pour l'Agent</h2>
        </div>
        <p className="text-slate-400">
          Instructions à suivre à chaque nouvelle session (fork)
        </p>
      </div>

      {/* PHRASE À COPIER-COLLER - PRINCIPALE */}
      <div className="bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border-2 border-emerald-500/50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Copy className="w-5 h-5 text-emerald-400" />
          🚀 Phrase à copier-coller au début de chaque nouvelle session
        </h3>
        
        <div className="bg-slate-900 border-2 border-dashed border-emerald-500/50 rounded-lg p-6 relative">
          <p className="text-lg text-emerald-400 font-medium pr-20">
            "{phraseACopier}"
          </p>
          <button
            onClick={copyPhraseACopier}
            className={`absolute top-3 right-3 px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
              copiedPhrase 
                ? 'bg-emerald-500 text-white' 
                : 'bg-emerald-600 hover:bg-emerald-500 text-white'
            }`}
          >
            {copiedPhrase ? (
              <>
                <CheckCircle size={16} />
                Copié !
              </>
            ) : (
              <>
                <Copy size={16} />
                Copier
              </>
            )}
          </button>
        </div>

        <p className="text-emerald-400/70 text-sm mt-4 text-center">
          ☝️ Copiez cette phrase et collez-la au tout début de chaque nouvelle conversation
        </p>
      </div>

      {/* Ce que l'agent doit faire */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Book className="w-5 h-5 text-emerald-400" />
          Ce que l'agent doit faire à chaque fork
        </h3>

        <div className="space-y-4">
          {steps.map((directive, idx) => (
            <div key={idx} className="flex items-start gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                directive.step === 1 
                  ? 'bg-red-500/30 border-2 border-red-500' 
                  : 'bg-emerald-500/20 border border-emerald-500/30'
              }`}>
                <span className={`font-bold ${directive.step === 1 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {directive.step}
                </span>
              </div>
              <div className="flex-1">
                <h4 className={`font-medium ${directive.step === 1 ? 'text-red-400' : 'text-white'}`}>
                  {directive.title}
                </h4>
                <p className="text-sm text-slate-400 mt-1">{directive.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Notes importantes */}
      {importantNotes.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-400 mb-2">Notes importantes</h4>
              <ul className="space-y-1">
                {importantNotes.map((note, idx) => (
                  <li key={idx} className="text-slate-400 text-sm flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>{note}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Workflow visuel */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-purple-400" />
          Cycle de travail
        </h3>

        <div className="flex items-center justify-between flex-wrap gap-4">
          {[
            { step: '1. Fork', desc: 'Vous collez la phrase', highlight: true },
            { step: '2. Sauvegarde', desc: 'Agent enregistre session précédente', highlight: true },
            { step: '3. Consulte', desc: 'Agent lit l\'historique' },
            { step: '4. Développe', desc: 'On travaille ensemble' },
            { step: '5. Documente', desc: 'Agent met à jour' },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="text-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 ${
                  item.highlight 
                    ? 'bg-red-500/30 border-2 border-red-500' 
                    : 'bg-purple-500/20 border border-purple-500/30'
                }`}>
                  <span className={`font-bold ${item.highlight ? 'text-red-400' : 'text-purple-400'}`}>
                    {idx + 1}
                  </span>
                </div>
                <p className={`text-xs font-medium ${item.highlight ? 'text-red-400' : 'text-white'}`}>
                  {item.step}
                </p>
                <p className="text-xs text-slate-500">{item.desc}</p>
              </div>
              {idx < 4 && (
                <ArrowRight size={16} className="text-slate-600" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Ancienne phrase (backup) */}
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
        <h3 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
          <Copy className="w-4 h-4" />
          Phrase alternative (ancienne)
        </h3>
        
        <div className="bg-slate-900/50 border border-dashed border-slate-600 rounded-lg p-4 relative">
          <p className="text-sm text-slate-500 pr-16">
            "{forkPhrase}"
          </p>
          <button
            onClick={copyForkPhrase}
            className={`absolute top-2 right-2 px-2 py-1 rounded text-xs flex items-center gap-1 transition-colors ${
              copied 
                ? 'bg-emerald-500 text-white' 
                : 'bg-slate-700 hover:bg-slate-600 text-slate-400'
            }`}
          >
            {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
            {copied ? 'Copié' : 'Copier'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentDirectives;
